import { ControlChartLimitsApi } from "src/api/generated/spc/controlChart/api/controlChartLimitsApi.ts";
import { AsyncForm } from "src/components/common/forms/AsyncForm.tsx";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { FormDateTimeField } from "src/components/common/forms/fields/FormDateTimeField.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faCalculator } from "@fortawesome/pro-regular-svg-icons";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import i18n from "i18next";
import { applyCalculatedLimits } from "./controlChartLimitCalculation.ts";
import { Box } from "@mui/material";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialogContext.tsx";

export interface ControlChartLimitCalculationFormProps extends FormCommonProps<number> {
	controlChartId: number;
	recordId?: number;
}

interface FormValues {
	recordCount: number;
	usageStart: string;
	note: string;
	xLcl: number;
	xAverage: number;
	xUcl: number;
	rLcl: number | null;
	rAverage: number | null;
	rUcl: number | null;
}

interface FormInitData {
	usageStart: string;
	limits: Awaited<ReturnType<typeof ControlChartLimitsApi.calculateLimits>>["limits"] | null;
}

export function ControlChartLimitCalculationForm({
	controlChartId,
	recordId,
	onCompleted,
	onFormEdited,
}: ControlChartLimitCalculationFormProps) {
	const { logErrorAndShowOnDialog } = useErrorDialog();

	return (
		<AsyncForm<FormInitData, FormValues, number>
			fetch={async () => {
				const [usageStart, limits] = await Promise.all([
					ControlChartLimitsApi.getLimitCalculationUsageStart({
						controlChartId,
						recordId: recordId ?? null,
					}),
					calculate(20)
						.then((result) => result.limits)
						.catch((error: unknown) => {
							logErrorAndShowOnDialog(error);
							return null;
						}),
				]);
				return { usageStart, limits };
			}}
			getDefaultValues={({ usageStart, limits }) => {
				const defaultValues: FormValues = {
					recordCount: 20,
					usageStart,
					note: "",
					xLcl: 0,
					xAverage: 0,
					xUcl: 0,
					rLcl: 0,
					rAverage: 0,
					rUcl: 0,
				};
				return limits == null ? defaultValues : applyCalculatedLimits(defaultValues, limits);
			}}
			submit={(values) =>
				ControlChartLimitsApi.saveLimits({
					input: {
						controlChartId,
						usageStart: values.usageStart,
						note: values.note,
						limits: {
							xLcl: values.xLcl,
							xAverage: values.xAverage,
							xUcl: values.xUcl,
							rLcl: values.rLcl,
							rAverage: values.rAverage,
							rUcl: values.rUcl,
						},
					},
				})
			}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			columns={3}
			render={({ control, getValues, setValue }) => (
				<>
					{recordId == null && (
						<FormNumberField
							control={control}
							name={"recordCount"}
							label={i18n.t("record_count")}
							type={"integer"}
							rules={{ min: { value: 1, message: i18n.t("number_must_be_greater_than_zero") } }}
							startNewGridRow
						/>
					)}
					<Box sx={{ gridColumn: "1 / -1" }}>
						<AsyncButton
							icon={faCalculator}
							label={i18n.t("calculate")}
							onClick={async () => {
								const result = await calculate(getValues("recordCount"));
								const values = applyCalculatedLimits(getValues(), result.limits);
								setValue("xLcl", values.xLcl, { shouldDirty: true });
								setValue("xAverage", values.xAverage, { shouldDirty: true });
								setValue("xUcl", values.xUcl, { shouldDirty: true });
								setValue("rLcl", values.rLcl, { shouldDirty: true });
								setValue("rAverage", values.rAverage, { shouldDirty: true });
								setValue("rUcl", values.rUcl, { shouldDirty: true });
							}}
						/>
					</Box>
					<FormNumberField control={control} name={"xLcl"} label={"LCL"} startNewGridRow />
					<FormNumberField control={control} name={"xAverage"} label={"x̄"} />
					<FormNumberField control={control} name={"xUcl"} label={"UCL"} />
					<FormNumberField control={control} name={"rLcl"} label={"LCL (R)"} startNewGridRow />
					<FormNumberField control={control} name={"rAverage"} label={"R"} />
					<FormNumberField control={control} name={"rUcl"} label={"UCL (R)"} />
					<FormTextField control={control} name={"note"} label={i18n.t("note")} multiline spanGridColumns />
					<FormDateTimeField control={control} name={"usageStart"} label={i18n.t("start_time")} />
				</>
			)}
			agentContext={{
				key: defineAgentUiContextKey("spc.controlChart.limitCalculation.form"),
				data: { controlChartId, recordId: recordId ?? null },
			}}
		/>
	);

	function calculate(recordCount: number) {
		return ControlChartLimitsApi.calculateLimits({
			controlChartId,
			recordCount: recordId == null ? recordCount : null,
			recordId: recordId ?? null,
		});
	}
}
